"use client";

import { FlexRowAlignCenter } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import { useTextToSpeechContextSelector } from "@/contexts/TextToSpeechContext";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import { themeColors } from "@/utils/themeColors";
import { useReviewModeContextSelector } from "@knowt/syncing/context/FlashcardReviewModeContext";
import { FlashcardSortingEnum, StudySessionType, StudySession } from "@knowt/syncing/graphql/schema";
import clsx from "clsx";
import { Scan, Settings, Shuffle, Star, Undo2 } from "lucide-react";
import dynamic from "next/dynamic";
import { useRouter } from "@bprogress/next";
import { memo, RefObject, useCallback, useEffect, useRef, useState } from "react";
import type { SwiperClass } from "swiper/react";
import FlashcardCarouselSkeletonLoading from "./components/FlashcardCarouselSkeletonLoading";
import ToggleSwitch from "@/components/ToggleSwitch";
import Circular from "@/components/Circular";
import { CarouselControlBtn, CarouselNavBtn, CarouselSortBtn } from "./styled/CarouselBtns";
import Mountable from "@/components/styled/div/Mountable";
import br from "@/styles/breakpoints.module.css";
import SortingIndicator from "./components/SortingIndicator";
import { toast } from "react-hot-toast";
import { iconSizes } from "@/utils/iconProps";

const FlashcardsCarouselList = dynamic(() => import("./components/CarouselList"), {
    loading: () => <FlashcardCarouselSkeletonLoading />,
    ssr: false,
});
const StudySettingsPopup = dynamic(() =>
    import("@/features/StudySettings/StudySettings").then(m => m.StudySettingsPopup)
);

const CarouselControls = ({
    swiperRef,
}: {
    swiperRef: RefObject<{ swiper: SwiperClass } | null>;
}) => {
    const router = useRouter();
    const { checkAuth } = useCheckAuth();
    const flashcardSet = useReviewModeContextSelector(context => context.flashcardSet);

    const flashcardSetId = flashcardSet?.flashcardSetId;

    const isSortingMode = useReviewModeContextSelector(state => state.isSortingMode);
    const flashcardIndex = useReviewModeContextSelector(state => state.flashcardIndex);
    const numOfCardsInCarousel = useReviewModeContextSelector(context => context.numOfCardsInCarousel);

    const isEndCard = flashcardIndex + 1 >= numOfCardsInCarousel;

    const starredFlashcardIds = useReviewModeContextSelector(context => context.starProps.starredFlashcardIds);
    const setFlashcardIndex = useReviewModeContextSelector(context => context.setFlashcardIndex);
    const updateStudySessionViewer = useReviewModeContextSelector(context => context.updateStudySessionViewer);
    const updateSettings = useReviewModeContextSelector(context => context.updateSettings);
    const setIsStudySettingsOpen = useReviewModeContextSelector(context => context.setIsStudySettingsOpen);
    const studySession = useReviewModeContextSelector(context => context.studySession);

    const onSort = useReviewModeContextSelector(context => context.onSort);
    const canRevertSort = useReviewModeContextSelector(context => context.canRevertSort);
    const onRevertSort = useReviewModeContextSelector(context => context.onRevertSort);
    const handleStopAudio = useTextToSpeechContextSelector(state => state.handleStopAudio);
    const settings = studySession?.settings.REVIEW;

    const renderNextButton = useCallback(() => {
        if (isSortingMode) {
            return (
                <CarouselSortBtn
                    isKnow={true}
                    onClick={() => {
                        onSort(FlashcardSortingEnum.KNOW);
                        swiperRef.current?.swiper.slideNext(200, true);
                    }}
                    disabled={flashcardIndex + 1 >= numOfCardsInCarousel}
                />
            );
        }
        return (
            <CarouselNavBtn
                isNextBtn={true}
                onClick={() => {
                    handleStopAudio();
                    if (!swiperRef.current) return;
                    setFlashcardIndex(flashcardIndex + 1);
                    (swiperRef.current as { swiper: SwiperClass }).swiper.slideNext(200, true);
                }}
                disabled={flashcardIndex + 1 >= numOfCardsInCarousel}
            />
        );
    }, [flashcardIndex, numOfCardsInCarousel, onSort, swiperRef, handleStopAudio, isSortingMode, setFlashcardIndex]);

    const renderPrevButton = useCallback(() => {
        if (isSortingMode) {
            return (
                <CarouselSortBtn
                    isDKnow={true}
                    onClick={() => {
                        onSort(FlashcardSortingEnum.D_KNOW);
                        (swiperRef.current as { swiper: SwiperClass }).swiper.slideNext(200, true);
                    }}
                    disabled={flashcardIndex + 1 >= numOfCardsInCarousel}
                />
            );
        }
        return (
            <CarouselNavBtn
                isPrevBtn={true}
                disabled={flashcardIndex === 0}
                onClick={() => {
                    handleStopAudio();
                    if (!swiperRef.current) return;
                    setFlashcardIndex(flashcardIndex - 1);
                    (swiperRef.current as { swiper: SwiperClass }).swiper.slidePrev(200, true);
                }}
            />
        );
    }, [flashcardIndex, onSort, swiperRef, handleStopAudio, isSortingMode, setFlashcardIndex, numOfCardsInCarousel]);

    const renderFlashcardNavigation = useCallback(
        () => (
            <FlexRowAlignCenter
                style={{
                    gap: "2.4rem",
                    backgroundColor: themeColors.neutralWhite,
                    padding: "0.8rem 1.2rem",
                    height: "4rem",
                    borderRadius: "4rem",
                    justifyContent: "center",
                }}>
                {renderPrevButton()}
                {flashcardIndex + 1 >= numOfCardsInCarousel ? (
                    "🎉🎉🎉"
                ) : (
                    <p
                        className="bodyBold1"
                        data-testid="auto-id-dbdb7033-cd5d-4e8a-a8f3-1e55ad40ef41">{`${Math.min(flashcardIndex + 1, numOfCardsInCarousel - 1)}/${numOfCardsInCarousel - 1}`}</p>
                )}
                {renderNextButton()}
            </FlexRowAlignCenter>
        ),
        [flashcardIndex, numOfCardsInCarousel, renderNextButton, renderPrevButton]
    );

    const renderSortingToggler = () => (
        <Circular className="secondaryTextBold1" style={{ height: "4rem", gap: "1.2rem", padding: "0 1rem" }}>
            Card Sorting
            <ToggleSwitch
                checked={isSortingMode}
                onChange={() => {
                    checkAuth({
                        event: AuthEvent.FLASHCARD_CAROUSEL,
                        onAuthSuccess: () => updateSettings({ sorting: !isSortingMode }),
                    });
                }}
            />
        </Circular>
    );

    const renderBtns = () => (
        <>
            {!!isSortingMode && (
                <CarouselControlBtn
                    Icon={Undo2}
                    onClick={() => {
                        onRevertSort();
                        setFlashcardIndex(flashcardIndex - 1);
                        (swiperRef.current as { swiper: SwiperClass }).swiper.slidePrev(200, true);
                    }}
                    tooltip={"Undo the last sort"}
                    disabled={!canRevertSort}
                />
            )}

            <CarouselControlBtn
                Icon={Star}
                isActive={settings?.starred}
                IconProps={{
                    size: iconSizes.SM,
                    fill: settings?.starred ? themeColors.icon2 : "none",
                    color: settings?.starred ? themeColors.icon2 : undefined,
                }}
                onClick={() =>
                    checkAuth(
                        () => {
                            if (!starredFlashcardIds?.length && !settings?.starred) {
                                return toast.error("You haven't starred any flashcards yet!");
                            }
                            updateSettings({ starred: !settings?.starred });
                            // reset the order in case it was set from legacy code
                            updateStudySessionViewer({ isShuffled: !!settings?.shuffled });
                        },
                        {
                            event: AuthEvent.STAR_FLASHCARD,
                        }
                    )
                }
                tooltip={settings?.starred ? "Study from all flashcards" : "Study from starred flashcards only"}
            />
            <CarouselControlBtn
                isActive={settings?.shuffled}
                Icon={Shuffle}
                onClick={() => {
                    checkAuth({
                        event: AuthEvent.SHUFFLE_FLASHCARDS,
                        onAuthSuccess: () => {
                            updateSettings({ shuffled: !settings?.shuffled });
                            updateStudySessionViewer({ isShuffled: !settings?.shuffled });
                        },
                    });
                }}
                tooltip={"Toggle Shuffle"}
            />

            <CarouselControlBtn
                Icon={Settings}
                onClick={() => setIsStudySettingsOpen(true)}
                tooltip={"Flashcards settings"}
            />

            <CarouselControlBtn
                Icon={Scan}
                onClick={() => {
                    checkAuth({
                        event: AuthEvent.STUDY_FLASHCARDS_REVIEW_MODE,
                        onAuthSuccess: () => router.push(`/study/flashcards/${flashcardSetId}/review`),
                    });
                }}
                tooltip={"Focus Flashcard in Review Mode"}
            />
        </>
    );

    return (
        <FlexRowAlignCenter
            style={{ marginTop: "2rem", position: "relative", gap: "1.2rem" }}
            className={clsx("scrollbar-thin", br.smDownScrollX)}>
            <FlexRowAlignCenter style={{ gap: "inherit", flex: 1 }}>
                {/* LEFT CAROUSEL CONTROL  */}
                <Mountable mount={!isEndCard}>{renderSortingToggler()}</Mountable>
            </FlexRowAlignCenter>

            {/* LEFT CAROUSEL MIDDLE  */}
            <FlexRowAlignCenter style={{ gap: "inherit", flex: 1, justifyContent: "center" }}>
                <Mountable mount={!isEndCard}>{renderFlashcardNavigation()}</Mountable>
            </FlexRowAlignCenter>

            {/* LEFT CAROUSEL RIGHT  */}
            <FlexRowAlignCenter style={{ gap: "inherit", flex: 1, justifyContent: "flex-end" }}>
                {renderBtns()}
            </FlexRowAlignCenter>
        </FlexRowAlignCenter>
    );
};

const FlashcardsCarousel = ({ fromOverview }: { fromOverview?: boolean }) => {
    const flashcardSet = useReviewModeContextSelector(context => context.flashcardSet);
    const flashcardIndex = useReviewModeContextSelector(state => state.flashcardIndex);

    const studySession = useReviewModeContextSelector(context => context.studySession);
    const startNewRound = useReviewModeContextSelector(context => context.startNewRound);
    const updateStudySessionViewer = useReviewModeContextSelector(context => context.updateStudySessionViewer);
    const isStudySettingsOpen = useReviewModeContextSelector(context => context.isStudySettingsOpen);
    const setIsStudySettingsOpen = useReviewModeContextSelector(context => context.setIsStudySettingsOpen);
    const commitViewerUpdates = useReviewModeContextSelector(context => context.commitViewerUpdates);
    const isViewerDirty = useReviewModeContextSelector(context => context.isViewerDirty);

    const isSortingMode = useReviewModeContextSelector(context => context.isSortingMode);
    const sortedKnow = useReviewModeContextSelector(context => context.sortedKnow);
    const sortedDontKnow = useReviewModeContextSelector(context => context.sortedDontKnow);
    const isSortingDirty = useReviewModeContextSelector(context => context.isSortingDirty);
    const commitSortingUpdates = useReviewModeContextSelector(context => context.commitSortingUpdates);

    const _isLoading = useReviewModeContextSelector(context => context.isLoading);
    const [extraLoading, setExtraLoading] = useState(true);

    useEffect(() => {
        // Small buffer to allow the big component to mount
        setTimeout(() => setExtraLoading(false), 350);
    }, []);

    const isLoading = extraLoading || _isLoading;

    // Autosave if we are using sorting. isDirty will be false otherwise
    useAutoSave({
        isDirty: () => isSortingDirty || isViewerDirty,
        save: async () => {
            commitSortingUpdates();
            commitViewerUpdates();
        },
        block: true,
        interval: 15_000,
    });

    const flashcardSetId = flashcardSet?.flashcardSetId;
    const swiperRef = useRef<{ swiper: SwiperClass } | null>(null);

    useEffect(() => {
        if (flashcardIndex === 0) {
            // scroll to the beginning
            swiperRef.current?.swiper?.slideTo(0, 200, true);
        }
    }, [flashcardIndex]);

    return (
        <div
            style={{ width: "100%" }}
            data-testid="auto-id-3ce25594-a0e4-4db9-968d-244b4cecff35">
            <Mountable mount={isSortingMode}>
                <SortingIndicator sortedKnow={sortedKnow} sortedDontKnow={sortedDontKnow} />
                <div
                    style={{ marginBottom: "1rem" }}
                    data-testid="auto-id-ea4358fa-1634-4a10-ad19-14d0584b4df5" />
            </Mountable>
            {/* THE CAROUSEL */}
            <FlexRowAlignCenter style={{ position: "relative" }}>
                {isLoading ? <FlashcardCarouselSkeletonLoading /> : <FlashcardsCarouselList swiperRef={swiperRef} />}
            </FlexRowAlignCenter>
            {/* CAROUSEL CONTROLS */}
            <CarouselControls swiperRef={swiperRef} />
            <LazyLoaded load={isStudySettingsOpen}>
                <StudySettingsPopup
                    isOpen={isStudySettingsOpen}
                    onClose={() => setIsStudySettingsOpen(false)}
                    studySessionType={StudySessionType.REVIEW}
                    updateStudySessionViewer={updateStudySessionViewer}
                    itemId={flashcardSetId as string}
                    startNewRound={startNewRound}
                    studySession={studySession as StudySession}
                    fromOverview={fromOverview}
                />
            </LazyLoaded>
        </div>
    );
};

export default memo(FlashcardsCarousel);
